/**
 * React Query hooks for Resource Hub
 * Implements caching strategy from research.md (Decision 4):
 * - Resources: 30min staleTime, 60min gcTime (static content, rarely changes)
 * - Favorites: Short cache handled in separate hook
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { fetchResources } from '../api/resourcesApi';
import type { ResourceQueryParams, ResourceListResponse } from '../types/filter.types';

/**
 * Cache configuration for resources (static educational content)
 * Based on research.md Decision 4: Tiered Caching Strategy
 */
const RESOURCE_CACHE_CONFIG = {
  staleTime: 30 * 60 * 1000, // 30 minutes - resources rarely change
  gcTime: 60 * 60 * 1000, // 60 minutes (React Query v5: cacheTime renamed to gcTime)
  refetchOnWindowFocus: false, // Don't refetch on tab focus
};

/**
 * Hook to fetch resources with filters and pagination
 * 
 * @param params - Query parameters (filters + pagination)
 * @param options.enabled - Set false to hold the request (e.g. while a filter
 *   from the URL is still being resolved)
 * @returns React Query result with resource list
 * 
 * @example
 * ```typescript
 * const { data, isLoading, isError } = useResources({
 *   category: ['Aging in Place'],
 *   type: ['Article'],
 *   page: 1,
 *   limit: 10,
 * });
 * ```
 */
export const useResources = (
  params?: ResourceQueryParams,
  options?: { enabled?: boolean },
): UseQueryResult<ResourceListResponse> => {
  return useQuery({
    queryKey: ['resources', params],
    queryFn: () => fetchResources(params),
    enabled: options?.enabled ?? true,
    ...RESOURCE_CACHE_CONFIG,
  });
};
